import React, { useState, useEffect } from 'react';
import { Avatar } from '@/components/ui/avatar';
import { getVisibleAdminNavigationItems, isAdminRoute } from './adminNavigation';
import { DOCUMENTS_HUB_ENABLED, HR_HUB_ENABLED, resolveMaintenanceHub } from '@/app/router/maintenanceHubMap';
import type { MaintenanceState } from '@/services/maintenanceState';
import { getVisibleDocumentHubSections, isDocumentHubSectionCurrent } from '@/features/documents/documentHubSections';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '@/components/ui/dropdown';
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from '@/components/ui/sidebar';
import {
  ArrowRightStartOnRectangleIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@heroicons/react/16/solid';
import {
  HomeIcon,
  QuestionMarkCircleIcon,
  WalletIcon,
  DocumentTextIcon,
  SunIcon,
  MoonIcon,
  ChartBarIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  BeakerIcon,
  BellIcon,
  BriefcaseIcon,
  DocumentArrowDownIcon,
  CloudArrowUpIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  UserCircleIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
  PhoneIcon,
  ShoppingBagIcon,
  BuildingOffice2Icon,
  LightBulbIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  CalendarDaysIcon,
} from '@heroicons/react/20/solid';

type ThemeMode = 'light' | 'dark' | 'system';

function HubMaintenanceIndicator({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-medium text-slate-600 dark:text-slate-300">
      <WrenchScrewdriverIcon className="h-3.5 w-3.5" aria-hidden="true" />
      <span>In manutenzione</span>
    </span>
  );
}

type AppSidebarProps = {
  adminMenuOpen: boolean;
  businessMenuOpen: boolean;
  currentSection: string;
  dependenciesMenuOpen: boolean;
  documentiMenuOpen: boolean;
  hrMenuOpen: boolean;
  initials: string;
  canAccessAdminArea: (requiredArea?: string) => boolean;
  maintenanceHubs: MaintenanceState['hubs'];
  sidebarCollapsed: boolean;
  themeMode: ThemeMode;
  unreadNotifications: number;
  userEmail: string | null;
  userName: string | null;
  userPhoto: string | null;
  permissions: string[];
  canAccessApp: (hubId: string, appId: string) => boolean;
  /** Nasconde Ordini e STDP a chi non ha nessuna struttura assegnata; assente = sempre visibili */
  hasStructureScope?: (appId: string) => boolean;
  canViewHub: (hubId: string) => boolean;
  navigate: (path: string) => void;
  onLogout: () => void;
  onOpenReleaseNotes: () => void;
  onOpenGuide: () => void;
  onOpenSupport: () => void;
  releaseNotesUnread: boolean;
  releaseNotesVersion: string;
  onToggleMode: () => void;
  onToggleSidebar: () => void;
  onToggleDependenciesMenu: () => void;
  onToggleBusinessMenu: () => void;
  onToggleDocumentiMenu: () => void;
  onToggleHRMenu: () => void;
  onToggleAdminMenu: () => void;
};

export function AppSidebar({
  adminMenuOpen,
  businessMenuOpen,
  currentSection,
  dependenciesMenuOpen,
  documentiMenuOpen,
  hrMenuOpen,
  initials,
  canAccessAdminArea,
  maintenanceHubs,
  sidebarCollapsed,
  themeMode,
  unreadNotifications,
  userEmail,
  userName,
  userPhoto,
  permissions,
  canAccessApp,
  hasStructureScope = () => true,
  canViewHub,
  navigate,
  onLogout,
  onOpenReleaseNotes,
  onOpenGuide,
  onOpenSupport,
  releaseNotesUnread,
  releaseNotesVersion,
  onToggleMode,
  onToggleSidebar,
  onToggleDependenciesMenu,
  onToggleBusinessMenu,
  onToggleDocumentiMenu,
  onToggleHRMenu,
  onToggleAdminMenu,
}: AppSidebarProps) {
  // L'hub amministrazione usa la fonte autorevole Platform Admin; il fallback legacy
  // resta limitato alla policy esistente.
  const showAdminSection = canAccessAdminArea();
  const visibleAdminItems = getVisibleAdminNavigationItems((area) => canAccessAdminArea(area));
  const isHubUnderMaintenance = (hubId: keyof MaintenanceState['hubs']) => maintenanceHubs[hubId].enabled;
  const hubLabel = (label: string, hubId: keyof MaintenanceState['hubs']) =>
    isHubUnderMaintenance(hubId) ? `${label} - In manutenzione` : label;
  const [pianiMenuOpen, setPianiMenuOpen] = useState(() => currentSection.startsWith('piani-terapeutici'));
  const [areaSanitariaMenuOpen, setAreaSanitariaMenuOpen] = useState(() => ['area-sanitaria', 'gestione-terapia', 'gestione-ordini', 'gestione-stdp', 'gestione-mmg'].includes(currentSection));

  useEffect(() => {
    if (currentSection.startsWith('piani-terapeutici')) setPianiMenuOpen(true);
    if (['area-sanitaria', 'gestione-terapia', 'gestione-ordini', 'gestione-stdp', 'gestione-mmg'].includes(currentSection)) setAreaSanitariaMenuOpen(true);
  }, [currentSection]);

  const maintenanceHubForSection = resolveMaintenanceHub(`/${currentSection}`);
  const isDocumentsSection = maintenanceHubForSection === 'documenti';
  const isHrSection = maintenanceHubForSection === 'hr';
  const isBusinessSection = maintenanceHubForSection === 'business';
  const isDependenciesSection = maintenanceHubForSection === 'dipendenze' || currentSection === 'gestione-mmg';
  const isAreaSanitariaSection = ['area-sanitaria', 'gestione-terapia', 'gestione-ordini', 'gestione-stdp', 'gestione-mmg'].includes(currentSection);
  const isPianiSection = currentSection.startsWith('piani-terapeutici');
  const isAdminSection = isAdminRoute(`/${currentSection}`);
  const documentLinks = getVisibleDocumentHubSections(permissions);
  const canOpenDocumentHub = canViewHub('documenti')
    || canAccessApp('documenti', 'console');

  return (
    <Sidebar
      className={`sidebar-transition ${sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}
      style={{ '--sidebar-width': sidebarCollapsed ? '4rem' : '16rem' } as React.CSSProperties}
    >
      <SidebarHeader>
        <button
          type="button"
          onClick={() => navigate('/')}
          className={`flex min-h-12 w-full items-center rounded-xl text-left transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-zinc-950/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 dark:hover:bg-white/5 ${sidebarCollapsed ? 'justify-center' : 'gap-3 px-1.5'}`}
          aria-label="Vai alla home"
        >
          <img
            src="/logo_simbolo.png"
            alt=""
            className={`sidebar-logo shrink-0 object-contain ${sidebarCollapsed ? 'h-6 w-6' : 'h-8 w-8'}`}
          />
          {!sidebarCollapsed && (
            <span className="sidebar-text sidebar-content-transition min-w-0">
              <span className="block text-sm font-semibold tracking-[-0.02em] text-zinc-950 dark:text-white">Nexus</span>
              <span className="block text-[11px] text-zinc-500 dark:text-zinc-400">Polo9 workspace</span>
            </span>
          )}
        </button>
      </SidebarHeader>
      <SidebarBody
        onClick={(e) => {
          if (typeof window !== 'undefined' && window.innerWidth < 1024) {
            return;
          }
          const target = e.target as HTMLElement;
          if (!target.closest('[role="menuitem"]') && !target.closest('a') && !target.closest('button')) {
            onToggleSidebar();
          }
        }}
        className="sidebar-scroll cursor-pointer max-lg:cursor-default max-lg:px-3 max-lg:py-4"
      >
        <SidebarSection>
          {!sidebarCollapsed && <SidebarHeading className="sidebar-text sidebar-content-transition">Spazi di lavoro</SidebarHeading>}
          <SidebarItem
            href="/"
            current={currentSection === 'home'}
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            title={sidebarCollapsed ? 'Home' : undefined}
            className={`sidebar-item ${sidebarCollapsed ? 'justify-center' : ''}`}
            colorScheme="teal"
          >
            <HomeIcon data-slot="icon" className="sidebar-icon" />
            <span className="sidebar-text sidebar-content-transition">{!sidebarCollapsed && <SidebarLabel>Home</SidebarLabel>}</span>
          </SidebarItem>

          {DOCUMENTS_HUB_ENABLED && canOpenDocumentHub && (
            <SidebarItem
              href="/documents"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                if (currentSection !== 'documents') navigate('/documents');
                onToggleDocumentiMenu();
              }}
              current={currentSection === 'documents'}
              ancestor={isDocumentsSection && currentSection !== 'documents'}
              aria-expanded={!sidebarCollapsed ? documentiMenuOpen : undefined}
              title={sidebarCollapsed ? hubLabel('Documenti', 'documenti') : undefined}
              aria-label={sidebarCollapsed ? hubLabel('Documenti', 'documenti') : undefined}
              className={`sidebar-item cursor-pointer ${sidebarCollapsed ? 'justify-center' : ''}`}
              colorScheme="teal"
            >
            <DocumentTextIcon data-slot="icon" className="sidebar-icon" />
            {!sidebarCollapsed && (
              <>
                <span className="sidebar-text sidebar-content-transition flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <SidebarLabel>Documenti</SidebarLabel>
                    <HubMaintenanceIndicator active={isHubUnderMaintenance('documenti')} />
                  </span>
                </span>
                <ChevronDownIcon
                  data-slot="icon"
                  className={`sidebar-icon transition-transform duration-200 ${documentiMenuOpen ? 'rotate-180' : ''}`}
                />
              </>
            )}
          </SidebarItem>
          )}

          {!sidebarCollapsed && documentiMenuOpen && DOCUMENTS_HUB_ENABLED && canOpenDocumentHub && (
            <div className="sidebar-nested ml-3 mt-1 space-y-0.5 border-l border-zinc-950/8 pl-2 dark:border-white/8">
              {documentLinks.map((item) => {
                const SectionIcon = item.icon;
                return (
                  <SidebarItem
                    key={item.id}
                    href={item.href}
                    current={isDocumentHubSectionCurrent(item.id, currentSection)}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(item.href);
                    }}
                    className="sidebar-item text-sm"
                    colorScheme="teal"
                  >
                    <SectionIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                    <span className="sidebar-text sidebar-content-transition">
                      <SidebarLabel>{item.label}</SidebarLabel>
                    </span>
                  </SidebarItem>
                );
              })}
            </div>
          )}

          {canViewHub('business') && (
            <SidebarItem
              href="/business"
              onClick={(e) => {
                e.preventDefault();
                onToggleBusinessMenu();
                if (!businessMenuOpen) navigate('/business');
              }}
              current={currentSection === 'business'}
              ancestor={isBusinessSection && currentSection !== 'business'}
              aria-expanded={!sidebarCollapsed ? businessMenuOpen : undefined}
              title={sidebarCollapsed ? hubLabel('Business', 'business') : undefined}
              aria-label={sidebarCollapsed ? hubLabel('Business', 'business') : undefined}
              className={`sidebar-item cursor-pointer ${sidebarCollapsed ? 'justify-center' : ''}`}
              colorScheme="coral"
            >
              <BriefcaseIcon data-slot="icon" className="sidebar-icon" />
              {!sidebarCollapsed && (
                <>
                  <span className="sidebar-text sidebar-content-transition flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <SidebarLabel>Business</SidebarLabel>
                      <HubMaintenanceIndicator active={isHubUnderMaintenance('business')} />
                    </span>
                  </span>
                  <ChevronDownIcon
                    data-slot="icon"
                    className={`sidebar-icon transition-transform duration-200 ${businessMenuOpen ? 'rotate-180' : ''}`}
                  />
                </>
              )}
            </SidebarItem>
          )}

          {!sidebarCollapsed && businessMenuOpen && (
            <div className="sidebar-nested ml-3 mt-1 space-y-0.5 border-l border-zinc-950/8 pl-2 dark:border-white/8">
              {canAccessApp('business', 'dashboard') && (
                <SidebarItem
                  href="/business/dashboard"
                  current={currentSection === 'business/dashboard' || currentSection === 'unified-dashboard'}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/business/dashboard');
                  }}
                  className="sidebar-item text-sm"
                  colorScheme="coral"
                >
                  <ChartBarIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                  <span className="sidebar-text">
                    <SidebarLabel>Dashboard</SidebarLabel>
                  </span>
                </SidebarItem>
              )}

              {canAccessApp('business', 'personale') && (
                <SidebarItem
                  href="/business/personale"
                  current={currentSection === 'business/personale' || currentSection === 'personale'}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/business/personale');
                  }}
                  className="sidebar-item text-sm"
                  colorScheme="coral"
                >
                  <UserGroupIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                  <span className="sidebar-text">
                    <SidebarLabel>Personale</SidebarLabel>
                  </span>
                </SidebarItem>
              )}

              {canAccessApp('business', 'portafoglio') && (
                <SidebarItem
                  href="/business/portafoglio"
                  current={currentSection === 'business/portafoglio' || currentSection === 'wallet'}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/business/portafoglio');
                  }}
                  className="sidebar-item text-sm"
                  colorScheme="coral"
                >
                  <WalletIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                  <span className="sidebar-text">
                    <SidebarLabel>Portafoglio</SidebarLabel>
                  </span>
                </SidebarItem>
              )}

              {canAccessApp('business', 'fornitori') && (
                <SidebarItem
                  href="/business/fornitori"
                  current={currentSection === 'business/fornitori'}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/business/fornitori');
                  }}
                  className="sidebar-item text-sm"
                  colorScheme="coral"
                >
                  <TruckIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                  <span className="sidebar-text">
                    <SidebarLabel>Fornitori</SidebarLabel>
                  </span>
                </SidebarItem>
              )}
            </div>
          )}

          {HR_HUB_ENABLED && canViewHub('hr') && (
            <SidebarItem
              href="/hr"
              onClick={(e) => {
                e.preventDefault();
                onToggleHRMenu();
                if (!hrMenuOpen) navigate('/hr');
              }}
              current={currentSection === 'hr'}
              ancestor={isHrSection && currentSection !== 'hr'}
              aria-expanded={!sidebarCollapsed ? hrMenuOpen : undefined}
              title={sidebarCollapsed ? hubLabel('HR', 'hr') : undefined}
              aria-label={sidebarCollapsed ? hubLabel('HR', 'hr') : undefined}
              className={`sidebar-item cursor-pointer ${sidebarCollapsed ? 'justify-center' : ''}`}
              colorScheme="violet"
            >
              <UserGroupIcon data-slot="icon" className="sidebar-icon" />
              {!sidebarCollapsed && (
                <>
                  <span className="sidebar-text sidebar-content-transition flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <SidebarLabel>HR</SidebarLabel>
                      <HubMaintenanceIndicator active={isHubUnderMaintenance('hr')} />
                    </span>
                  </span>
                  <ChevronDownIcon
                    data-slot="icon"
                    className={`sidebar-icon transition-transform duration-200 ${hrMenuOpen ? 'rotate-180' : ''}`}
                  />
                </>
              )}
            </SidebarItem>
          )}

          {!sidebarCollapsed && hrMenuOpen && HR_HUB_ENABLED && canViewHub('hr') && (
            <div className="sidebar-nested ml-3 mt-1 space-y-0.5 border-l border-zinc-950/8 pl-2 dark:border-white/8">
              {canAccessApp('hr', 'soci-dipendenti') && (
                <SidebarItem
                  href="/hr/soci-e-dipendenti"
                  current={currentSection === 'hr/soci-e-dipendenti'}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/hr/soci-e-dipendenti');
                  }}
                  className="sidebar-item text-sm"
                  colorScheme="violet"
                >
                  <UserGroupIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                  <span className="sidebar-text"><SidebarLabel>Soci e dipendenti</SidebarLabel></span>
                </SidebarItem>
              )}
              {canAccessApp('hr', 'cedolini') && (
                <SidebarItem
                  href="/hr/cedolini"
                  current={currentSection === 'hr/cedolini'}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/hr/cedolini');
                  }}
                  className="sidebar-item text-sm"
                  colorScheme="violet"
                >
                  <DocumentTextIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                  <span className="sidebar-text"><SidebarLabel>Cedolini</SidebarLabel></span>
                </SidebarItem>
              )}
              {canAccessApp('hr', 'cedolini') && (
                <SidebarItem
                  href="/hr/carica-dati"
                  current={currentSection === 'hr/carica-dati'}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/hr/carica-dati');
                  }}
                  className="sidebar-item text-sm"
                  colorScheme="violet"
                >
                  <DocumentTextIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                  <span className="sidebar-text"><SidebarLabel>Carica dati</SidebarLabel></span>
                </SidebarItem>
              )}
              {canAccessApp('hr', 'turni') && (
                <SidebarItem
                  href="/hr/turni"
                  current={currentSection === 'hr/turni'}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/hr/turni');
                  }}
                  className="sidebar-item text-sm"
                  colorScheme="violet"
                >
                  <CalendarDaysIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                  <span className="sidebar-text"><SidebarLabel>Turni</SidebarLabel></span>
                </SidebarItem>
              )}
            </div>
          )}

          {canViewHub('dipendenze') && (
            <SidebarItem
              href="/dipendenze"
              onClick={(e) => {
                e.preventDefault();
                onToggleDependenciesMenu();
                if (!dependenciesMenuOpen) navigate('/dipendenze');
              }}
              current={currentSection === 'dipendenze'}
              ancestor={isDependenciesSection && currentSection !== 'dipendenze'}
              aria-expanded={!sidebarCollapsed ? dependenciesMenuOpen : undefined}
              title={sidebarCollapsed ? hubLabel('Dipendenze', 'dipendenze') : undefined}
              aria-label={sidebarCollapsed ? hubLabel('Dipendenze', 'dipendenze') : undefined}
              className={`sidebar-item cursor-pointer ${sidebarCollapsed ? 'justify-center' : ''}`}
              colorScheme="cyan"
            >
              <BeakerIcon data-slot="icon" className="sidebar-icon" />
              {!sidebarCollapsed && (
                <>
                  <span className="sidebar-text sidebar-content-transition flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <SidebarLabel>Dipendenze</SidebarLabel>
                      <HubMaintenanceIndicator active={isHubUnderMaintenance('dipendenze')} />
                    </span>
                  </span>
                  <ChevronDownIcon
                    data-slot="icon"
                    className={`sidebar-icon transition-transform duration-200 ${dependenciesMenuOpen ? 'rotate-180' : ''}`}
                  />
                </>
              )}
            </SidebarItem>
          )}

          {!sidebarCollapsed && dependenciesMenuOpen && (
            <div className="sidebar-nested ml-3 mt-1 space-y-0.5 border-l border-zinc-950/8 pl-2 dark:border-white/8">
              {canAccessApp('dipendenze', 'dashboard') && (
                <SidebarItem
                  current={currentSection === 'dipendenze/dashboard'}
                  onClick={() => navigate('/dipendenze/dashboard')}
                  className="sidebar-item text-sm cursor-pointer"
                  colorScheme="cyan"
                >
                  <ChartBarIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                  <span className="sidebar-text"><SidebarLabel>Dashboard</SidebarLabel></span>
                </SidebarItem>
              )}
              {canAccessApp('dipendenze', 'gestione-assistiti') && (
                <SidebarItem
                  href="/dipendenze/gestione-assistiti"
                  current={currentSection === 'dipendenze/gestione-assistiti'}
                  onClick={(e) => { e.preventDefault(); navigate('/dipendenze/gestione-assistiti'); }}
                  className="sidebar-item text-sm cursor-pointer"
                  colorScheme="cyan"
                >
                  <UserCircleIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                  <span className="sidebar-text"><SidebarLabel>Gestione Assistiti</SidebarLabel></span>
                </SidebarItem>
              )}
              {canAccessApp('dipendenze', 'gestione-strutture') && (
                <SidebarItem
                  href="/dipendenze/gestione-strutture"
                  current={currentSection === 'dipendenze/gestione-strutture'}
                  onClick={(e) => { e.preventDefault(); navigate('/dipendenze/gestione-strutture'); }}
                  className="sidebar-item text-sm cursor-pointer"
                  colorScheme="cyan"
                >
                  <BuildingOffice2Icon data-slot="icon" className="sidebar-icon w-4 h-4" />
                  <span className="sidebar-text"><SidebarLabel>Gestione Strutture</SidebarLabel></span>
                </SidebarItem>
              )}

              {canAccessApp('dipendenze', 'area-sanitaria') && (
                <>
                  <SidebarItem
                    onClick={() => {
                      setAreaSanitariaMenuOpen(prev => !prev);
                      if (!areaSanitariaMenuOpen) navigate('/area-sanitaria');
                    }}
                    className="sidebar-item text-sm cursor-pointer"
                    colorScheme="cyan"
                    current={currentSection === 'area-sanitaria'}
                    ancestor={isAreaSanitariaSection && currentSection !== 'area-sanitaria'}
                    aria-expanded={areaSanitariaMenuOpen}
                  >
                    <BeakerIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                    <span className="sidebar-text flex-1"><SidebarLabel>Area Sanitaria</SidebarLabel></span>
                    <ChevronDownIcon data-slot="icon" className={`sidebar-icon w-3 h-3 transition-transform duration-200 ${areaSanitariaMenuOpen ? 'rotate-180' : ''}`} />
                  </SidebarItem>
                  {areaSanitariaMenuOpen && (
                    <div className="sidebar-nested sidebar-nested-deep ml-3 mt-1 space-y-0.5 border-l border-cyan-500/20 pl-2">
                      {canAccessApp('dipendenze', 'gestione-terapie') && (
                        <SidebarItem
                          href="/gestione-terapia"
                          current={currentSection === 'gestione-terapia'}
                          onClick={(event) => {
                            event.preventDefault();
                            navigate('/gestione-terapia');
                          }}
                          className="sidebar-item text-sm"
                          colorScheme="cyan"
                        >
                          <BeakerIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                          <span className="sidebar-text"><SidebarLabel>Gestione Terapie</SidebarLabel></span>
                        </SidebarItem>
                      )}
                      {canAccessApp('dipendenze', 'gestione-stdp') && hasStructureScope('gestione-stdp') && (
                        <SidebarItem
                          href="/gestione-stdp"
                          current={currentSection === 'gestione-stdp'}
                          onClick={(event) => {
                            event.preventDefault();
                            navigate('/gestione-stdp');
                          }}
                          className="sidebar-item text-sm"
                          colorScheme="cyan"
                        >
                          <ClipboardDocumentListIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                          <span className="sidebar-text"><SidebarLabel>Gestione STDP</SidebarLabel></span>
                        </SidebarItem>
                      )}
                      {canAccessApp('dipendenze', 'gestione-ordini') && hasStructureScope('gestione-ordini') && (
                        <SidebarItem
                          href="/gestione-ordini"
                          current={currentSection === 'gestione-ordini'}
                          onClick={(event) => {
                            event.preventDefault();
                            navigate('/gestione-ordini');
                          }}
                          className="sidebar-item text-sm"
                          colorScheme="cyan"
                        >
                          <ShoppingBagIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                          <span className="sidebar-text"><SidebarLabel>Gestione Ordini</SidebarLabel></span>
                        </SidebarItem>
                      )}
                      {canAccessApp('dipendenze', 'gestione-mmg') && (
                        <SidebarItem
                          href="/gestione-mmg"
                          current={currentSection === 'gestione-mmg'}
                          onClick={(event) => {
                            event.preventDefault();
                            navigate('/gestione-mmg');
                          }}
                          className="sidebar-item text-sm"
                          colorScheme="cyan"
                        >
                          <BuildingOffice2Icon data-slot="icon" className="sidebar-icon w-4 h-4" />
                          <span className="sidebar-text"><SidebarLabel>Gestione MMG</SidebarLabel></span>
                        </SidebarItem>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Piani Terapeutici — espandibile inline */}
              {canAccessApp('dipendenze', 'piani-terapeutici') && (
                <>
                  <SidebarItem
                    onClick={() => {
                      setPianiMenuOpen(prev => !prev);
                      if (!pianiMenuOpen) navigate('/piani-terapeutici');
                    }}
                    className="sidebar-item text-sm cursor-pointer"
                    colorScheme="cyan"
                    current={currentSection === 'piani-terapeutici'}
                    ancestor={isPianiSection && currentSection !== 'piani-terapeutici'}
                    aria-expanded={pianiMenuOpen}
                  >
                    <DocumentArrowDownIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                    <span className="sidebar-text flex-1"><SidebarLabel>Piani Terapeutici</SidebarLabel></span>
                    <ChevronDownIcon data-slot="icon" className={`sidebar-icon w-3 h-3 transition-transform duration-200 ${pianiMenuOpen ? 'rotate-180' : ''}`} />
                  </SidebarItem>
                  {pianiMenuOpen && (
                    <div className="sidebar-nested sidebar-nested-deep ml-3 mt-1 space-y-0.5 border-l border-cyan-500/20 pl-2">
                      {canAccessApp('dipendenze', 'pt-carica-dati') && (
                        <SidebarItem href="/piani-terapeutici/carica-dati" current={currentSection === 'piani-terapeutici/carica-dati'} onClick={(e) => { e.preventDefault(); navigate('/piani-terapeutici/carica-dati'); }} className="sidebar-item text-sm" colorScheme="cyan">
                          <CloudArrowUpIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                          <span className="sidebar-text"><SidebarLabel>Carica Dati</SidebarLabel></span>
                        </SidebarItem>
                      )}
                      {canAccessApp('dipendenze', 'pt-questionari') && (
                        <SidebarItem href="/piani-terapeutici/questionari" current={currentSection === 'piani-terapeutici/questionari'} onClick={(e) => { e.preventDefault(); navigate('/piani-terapeutici/questionari'); }} className="sidebar-item text-sm" colorScheme="cyan">
                          <ClipboardDocumentCheckIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                          <span className="sidebar-text"><SidebarLabel>Questionari</SidebarLabel></span>
                        </SidebarItem>
                      )}
                      {canAccessApp('dipendenze', 'pt-piani') && (
                        <SidebarItem href="/piani-terapeutici/piani" current={currentSection.startsWith('piani-terapeutici/piani')} onClick={(e) => { e.preventDefault(); navigate('/piani-terapeutici/piani'); }} className="sidebar-item text-sm" colorScheme="cyan">
                          <DocumentDuplicateIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                          <span className="sidebar-text"><SidebarLabel>Piani</SidebarLabel></span>
                        </SidebarItem>
                      )}
                      {canAccessApp('dipendenze', 'pt-assistente-ia') && (
                        <SidebarItem href="/piani-terapeutici/assistente-ia" current={currentSection === 'piani-terapeutici/assistente-ia'} onClick={(e) => { e.preventDefault(); navigate('/piani-terapeutici/assistente-ia'); }} className="sidebar-item text-sm" colorScheme="cyan">
                          <SparklesIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                          <span className="sidebar-text"><SidebarLabel>Assistente IA</SidebarLabel></span>
                        </SidebarItem>
                      )}
                    </div>
                  )}
                </>
              )}

              {canAccessApp('dipendenze', 'info-contatti') && (
                <SidebarItem
                  onClick={() => navigate('/contatti-utili')}
                  className="sidebar-item text-sm cursor-pointer"
                  colorScheme="cyan"
                  current={currentSection === 'contatti-utili'}
                >
                  <PhoneIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                  <span className="sidebar-text"><SidebarLabel>Info e Contatti Utili</SidebarLabel></span>
                </SidebarItem>
              )}
            </div>
          )}
          {canViewHub('applicativi') && (
            <SidebarItem
              href="/applicativi"
              onClick={(e) => {
                e.preventDefault();
                navigate('/applicativi');
              }}
              current={currentSection.startsWith('applicativi')}
              title={sidebarCollapsed ? hubLabel('Applicativi', 'applicativi') : undefined}
              aria-label={sidebarCollapsed ? hubLabel('Applicativi', 'applicativi') : undefined}
              className={`sidebar-item cursor-pointer ${sidebarCollapsed ? 'justify-center' : ''}`}
              colorScheme="amber"
            >
              <ShoppingBagIcon data-slot="icon" className="sidebar-icon" />
              {!sidebarCollapsed && (
                <span className="sidebar-text sidebar-content-transition flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <SidebarLabel>Applicativi</SidebarLabel>
                    <HubMaintenanceIndicator active={isHubUnderMaintenance('applicativi')} />
                  </span>
                </span>
              )}
            </SidebarItem>
          )}
        </SidebarSection>
        <SidebarSpacer />
        <SidebarSection>
          {!sidebarCollapsed && <SidebarHeading className="sidebar-text sidebar-content-transition">Piattaforma</SidebarHeading>}
          {showAdminSection && (
            <>
              <SidebarItem
                href="/admin"
                onClick={(e) => {
                  e.preventDefault();
                  onToggleAdminMenu();
                  if (!adminMenuOpen) navigate('/admin');
                }}
                current={currentSection === 'admin'}
                ancestor={isAdminSection && currentSection !== 'admin'}
                aria-expanded={!sidebarCollapsed ? adminMenuOpen : undefined}
                title={sidebarCollapsed ? 'Amministrazione' : undefined}
                className={`sidebar-item cursor-pointer ${sidebarCollapsed ? 'justify-center' : ''}`}
                colorScheme="gray"
              >
                <Cog6ToothIcon data-slot="icon" className="sidebar-icon" />
                {!sidebarCollapsed && (
                  <>
                    <span className="sidebar-text sidebar-content-transition flex-1">
                      <SidebarLabel>Amministrazione</SidebarLabel>
                    </span>
                    <ChevronDownIcon data-slot="icon" className={`sidebar-icon transition-transform duration-200 ${adminMenuOpen ? 'rotate-180' : ''}`} />
                  </>
                )}
              </SidebarItem>

              {!sidebarCollapsed && adminMenuOpen && visibleAdminItems.length > 0 && (
                <div className="sidebar-nested ml-3 mt-1 space-y-0.5 border-l border-zinc-950/8 pl-2 dark:border-white/8">
                  {visibleAdminItems.map((item) => {
                    const ItemIcon = item.icon;
                    const current = currentSection === item.href.slice(1) || item.activePaths.includes(currentSection);
                    return (
                      <SidebarItem
                        key={item.key}
                        href={item.href}
                        current={current}
                        onClick={(event) => { event.preventDefault(); navigate(item.href); }}
                        className="sidebar-item text-sm"
                        colorScheme="gray"
                      >
                        <ItemIcon data-slot="icon" className="sidebar-icon w-4 h-4" />
                        <span className="sidebar-text"><SidebarLabel>{item.title}</SidebarLabel></span>
                      </SidebarItem>
                    );
                  })}
                </div>
              )}
            </>
          )}

          <SidebarItem
            onClick={(e) => {
              e.preventDefault();
              onOpenReleaseNotes();
            }}
            title={sidebarCollapsed ? `Novità v${releaseNotesVersion}` : undefined}
            aria-label={sidebarCollapsed ? 'Apri il Centro Novità' : undefined}
            className={`sidebar-item cursor-pointer ${sidebarCollapsed ? 'justify-center' : ''}`}
            colorScheme="teal"
          >
            <LightBulbIcon data-slot="icon" className="sidebar-icon" />
            {!sidebarCollapsed && (
              <span className="sidebar-text sidebar-content-transition flex min-w-0 flex-1 items-center justify-between gap-2">
                <SidebarLabel>Novità</SidebarLabel>
                <span className="shrink-0 text-[10px] font-semibold tabular-nums text-zinc-400 dark:text-zinc-500">v{releaseNotesVersion}</span>
              </span>
            )}
            {releaseNotesUnread && (
              <>
                <span
                  className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-teal-500 ring-2 ring-white dark:ring-zinc-900"
                  aria-hidden="true"
                />
                <span className="sr-only">Novità non lette</span>
              </>
            )}
          </SidebarItem>

          <SidebarItem
            onClick={(e) => {
              e.preventDefault();
              onOpenGuide();
            }}
            title={sidebarCollapsed ? 'Guida' : undefined}
            aria-label={sidebarCollapsed ? 'Apri la Guida' : undefined}
            className={`sidebar-item cursor-pointer ${sidebarCollapsed ? 'justify-center' : ''}`}
            colorScheme="violet"
          >
            <SparklesIcon data-slot="icon" className="sidebar-icon" />
            <span className="sidebar-text sidebar-content-transition">{!sidebarCollapsed && <SidebarLabel>Guida</SidebarLabel>}</span>
          </SidebarItem>

          <SidebarItem
            onClick={(e) => {
              e.preventDefault();
              onOpenSupport();
            }}
            title={sidebarCollapsed ? 'Supporto' : undefined}
            className={`sidebar-item cursor-pointer ${sidebarCollapsed ? 'justify-center' : ''}`}
            colorScheme="blue"
          >
            <QuestionMarkCircleIcon data-slot="icon" className="sidebar-icon" />
            <span className="sidebar-text sidebar-content-transition">{!sidebarCollapsed && <SidebarLabel>Supporto</SidebarLabel>}</span>
          </SidebarItem>

          <SidebarItem
            onClick={(e) => {
              e.preventDefault();
              onToggleMode();
            }}
            title={sidebarCollapsed ? (themeMode === 'dark' ? 'Tema Chiaro' : 'Tema Scuro') : undefined}
            className={`sidebar-item cursor-pointer ${sidebarCollapsed ? 'justify-center' : ''}`}
          >
            {themeMode === 'dark' ? (
              <>
                <SunIcon data-slot="icon" className="sidebar-icon" />
                <span className="sidebar-text sidebar-content-transition">{!sidebarCollapsed && <SidebarLabel>Tema Chiaro</SidebarLabel>}</span>
              </>
            ) : (
              <>
                <MoonIcon data-slot="icon" className="sidebar-icon" />
                <span className="sidebar-text sidebar-content-transition">{!sidebarCollapsed && <SidebarLabel>Tema Scuro</SidebarLabel>}</span>
              </>
            )}
          </SidebarItem>

          <SidebarItem
            onClick={(e) => {
              e.preventDefault();
              onToggleSidebar();
            }}
            title={sidebarCollapsed ? 'Espandi' : 'Comprimi'}
            className={`sidebar-item cursor-pointer max-lg:hidden ${sidebarCollapsed ? 'justify-center' : ''}`}
            colorScheme="gray"
          >
            {sidebarCollapsed ? (
              <ChevronRightIcon data-slot="icon" className="sidebar-icon" />
            ) : (
              <>
                <ChevronLeftIcon data-slot="icon" className="sidebar-icon" />
                <span className="sidebar-text sidebar-content-transition"><SidebarLabel>Comprimi</SidebarLabel></span>
              </>
            )}
          </SidebarItem>
        </SidebarSection>
      </SidebarBody>
      <SidebarFooter className={`max-lg:hidden ${sidebarCollapsed ? 'p-2' : ''}`}>
        <Dropdown>
          <DropdownButton as={SidebarItem} className={`sidebar-item sidebar-profile ${sidebarCollapsed ? 'justify-center' : ''}`}>
            {sidebarCollapsed ? (
              <Avatar src={userPhoto || undefined} initials={initials} className="size-8 sidebar-avatar" square alt="" />
            ) : (
              <>
                <span className="flex min-w-0 items-center gap-3 sidebar-content-transition">
                  <Avatar src={userPhoto || undefined} initials={initials} className="size-10 sidebar-avatar" square alt="" />
                  <span className="min-w-0 sidebar-text">
                    <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">{userName || 'Utente'}</span>
                    <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">{userEmail || ''}</span>
                  </span>
                </span>
                <ChevronUpIcon data-slot="icon" className="sidebar-icon" />
              </>
            )}
          </DropdownButton>
          <DropdownMenu className={sidebarCollapsed ? 'min-w-52' : 'w-56'} anchor="top start">
            {showAdminSection && (
              <>
                <DropdownItem
                  onClick={() => {
                    navigate('/admin');
                    setTimeout(() => {
                      const notificheTab = document.querySelector('[data-section="notifiche"]');
                      if (notificheTab) {
                        (notificheTab as HTMLElement).click();
                      }
                    }, 100);
                  }}
                  className="transition-colors hover:bg-zinc-100 data-focus:!bg-zinc-100 data-focus:!text-zinc-950 dark:hover:bg-white/10 dark:data-focus:!bg-white/10 dark:data-focus:!text-white"
                >
                  <div className="relative">
                    <BellIcon data-slot="icon" className="text-zinc-500 dark:text-zinc-400" />
                    {unreadNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                        {unreadNotifications > 9 ? '9+' : unreadNotifications}
                      </span>
                    )}
                  </div>
                  <DropdownLabel>
                    Notifiche
                    {unreadNotifications > 0 && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                        {unreadNotifications}
                      </span>
                    )}
                  </DropdownLabel>
                </DropdownItem>
                <DropdownDivider />
              </>
            )}
            <DropdownItem
              onClick={onLogout}
              className="transition-colors hover:bg-red-50 hover:text-red-600 data-focus:!bg-red-50 data-focus:!text-red-700 dark:hover:bg-red-500/10 dark:hover:text-red-300 dark:data-focus:!bg-red-500/10 dark:data-focus:!text-red-300"
            >
              <ArrowRightStartOnRectangleIcon data-slot="icon" className="text-zinc-500 dark:text-zinc-400 group-hover:text-red-500" />
              <DropdownLabel>Esci</DropdownLabel>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </SidebarFooter>
    </Sidebar>
  );
}

export default AppSidebar;
